import { useState } from 'react';
import styled, { css } from 'styled-components';

import { font_sizes } from '@hookdeck/theme';

import { useCopyToClipboard } from '../../utils/copy';
import Button from './base/Button';
import Icon, { StyledIcon } from './base/Icon';
import Text from './base/Text';
import { getStyledUtils, StyledUtilsProps } from './helpers/StyledUtils';

interface Props extends StyledUtilsProps {
  variant?: 'command_list' | 'default';
  value: string;
  valueElement?: React.ReactNode;
  secret?: boolean;
  reveal_on_hover?: boolean;
  mono?: boolean;
  disabled?: boolean;
  help?: string;
  borderless?: boolean;
  ghost?: boolean;
  wrap?: boolean;
  text_size?: keyof typeof font_sizes;
  // Number of visible values to show when the field is secret. Defaults to 3.
  visible_values?: number;
  // What screen readers call the ghost variant's copy button, e.g. "Copy token".
  copy_label?: string;
}

const StyledCopyableFieldValue = styled.button<{
  borderless?: boolean;
  ghost?: boolean;
  $wrap?: boolean;
  variant?: 'command_list' | 'default';
}>(
  ({
    theme,
    borderless = false,
    ghost = false,
    $wrap = false,
    variant = 'default',
    ...props
  }) => css`
    ${getStyledUtils({ theme, ...props })}
    padding: ${ghost ? '0' : `${theme.spacing(1.5)} ${theme.spacing(3)}`};
    background-color: ${ghost
      ? 'transparent'
      : variant === 'command_list'
        ? theme.colors.surface.base.variant_surface_3
        : theme.colors.surface.base.variant_surface_2};
    border-radius: ${borderless
      ? 0
      : variant === 'command_list'
        ? theme.radius.small
        : theme.radius.normal};
    display: flex;
    align-items: ${$wrap ? 'flex-start' : 'center'};
    gap: ${theme.spacing(2)};
    min-width: 0;
    border: ${variant === 'command_list' && !borderless ? theme.border : '0'};
    box-shadow: ${borderless || ghost || variant === 'command_list'
      ? 'none'
      : theme.shadows.neutral.base};
    width: 100%;
    box-sizing: border-box;
    /* Ghost has no background/radius to clip, so keep overflow visible — otherwise the copy
       Button's hover box-shadow border (it sits flush at the edge) gets clipped. */
    overflow: ${ghost || $wrap ? 'visible' : 'hidden'};

    &:hover {
      background-color: ${ghost ? 'transparent' : theme.colors.surface.base.hover.neutral};
      cursor: ${ghost ? 'auto' : 'pointer'};
    }

    &:focus-visible {
      outline: none;
      box-shadow: ${theme.shadows.input.focused};
    }

    &:disabled {
      color: ${theme.colors.on.neutral.disabled};
      ${StyledIcon} {
        fill: ${theme.colors.on.neutral.disabled};
        color: ${theme.colors.on.neutral.disabled};
      }
      cursor: not-allowed;

      &:hover {
        background-color: ${theme.colors.surface.base.variant_surface_2};
      }
    }
  `,
);

const DEFAULT_VISIBLE_VALUES = 3;

const CopyableField: React.FC<Props> = ({
  value,
  help,
  secret = false,
  reveal_on_hover = true,
  mono,
  valueElement,
  disabled = false,
  ghost = false,
  wrap = false,
  visible_values = DEFAULT_VISIBLE_VALUES,
  text_size,
  copy_label,
  ...props
}) => {
  const [show_secret, setShowSecret] = useState(false);
  const copyToClipboard = useCopyToClipboard();
  const is_masked = secret && (reveal_on_hover ? !show_secret : true);

  const handleCopy = () => copyToClipboard(Array.isArray(value) ? value.join(' && ') : value);

  return (
    <>
      <StyledCopyableFieldValue
        {...props}
        // The ghost variant nests a real Button for the copy action, so the container can't be a
        // button itself (nested buttons are invalid). Render it as a plain, non-clickable div.
        as={ghost ? 'div' : 'button'}
        type={ghost ? undefined : 'button'}
        onClick={ghost ? undefined : handleCopy}
        disabled={ghost ? undefined : disabled}
        ghost={ghost}
        $wrap={wrap}>
        <div style={{ flex: 1, minWidth: 0, overflow: wrap ? 'visible' : 'hidden' }}>
          {valueElement ? (
            valueElement
          ) : (
            <Text
              as="p"
              ellipsis={!wrap}
              text_wrap={wrap}
              word_break={wrap ? 'break-all' : undefined}
              m={0}
              mono={mono}
              size={text_size}
              white_space={wrap ? 'pre-wrap' : undefined}
              onMouseOver={() => secret && reveal_on_hover && setShowSecret(true)}
              onMouseOut={() => secret && reveal_on_hover && setShowSecret(false)}
              align="left">
              {is_masked
                ? `${value?.substring(0, visible_values || DEFAULT_VISIBLE_VALUES)}${value?.substring(visible_values || DEFAULT_VISIBLE_VALUES).replace(/./g, '*')}`
                : value}
            </Text>
          )}
        </div>
        {ghost ? (
          <Button
            ghost
            icon="copy"
            aria-label={copy_label}
            onClick={handleCopy}
            disabled={disabled}
            style={{ flexShrink: 0 }}
          />
        ) : (
          <Icon icon="copy" muted pointer style={{ flexShrink: 0 }} p={wrap ? 0.5 : 0} />
        )}
      </StyledCopyableFieldValue>
      {help && (
        <Text m={{ t: 1, b: 0 }} size="xs" as="p" muted>
          <Icon icon="info" left={1} />
          {help}
        </Text>
      )}
    </>
  );
};

export default CopyableField;
